import { useState, useMemo, useEffect } from 'react';
import { FaScroll, FaChartBar } from 'react-icons/fa';
import { ContentType } from '@/constants/constants';
import { ArticlesWithMeta, ContentTab, ContentTypes, DashboardData } from '@/types/util.types';

interface UseAuthorTabsProps {
  authorId?: number;
  authorArticles: ArticlesWithMeta | undefined;
  authorDashboards?: DashboardData;
}

/**
 * Custom hook to manage author content tabs (Articles, Dashboard)
 * Handles tab state, content filtering, and related utilities
 */
export const useAuthorTabs = ({ authorArticles, authorDashboards }: UseAuthorTabsProps) => {
  const [activeTab, setActiveTab] = useState<ContentTypes>(ContentType.Articles);

  /**
   * Define all possible content tabs with their properties
   */
  const contentTabs: ContentTab[] = useMemo(
    () => [
      {
        id: ContentType.Articles,
        label: 'Articles',
        icon: <FaScroll />,
        hasContent: true,
      },
      {
        id: ContentType.Dashboard,
        label: 'Dashboard',
        icon: <FaChartBar />,
        hasContent: authorDashboards?.hasDashboards || false,
      },
    ],
    [authorDashboards]
  );

  /**
   * Filter tabs to only show those with content
   */
  const availableTabs: ContentTab[] = useMemo(() => {
    return contentTabs.filter((tab) => tab.hasContent);
  }, [contentTabs]);

  /**
   * Ensure active tab is valid and automatically switch to first available tab if needed
   */
  useEffect(() => {
    const activeTabExists = availableTabs.some((tab) => tab.id === activeTab);
    if (!activeTabExists && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  /**
   * Handle tab change when user clicks a tab
   */
  const handleTabChange = (tabId: ContentTypes) => {
    setActiveTab(tabId);
  };

  /**
   * Get the count of content items for the active tab
   */
  const getContentCount = () => {
    switch (activeTab) {
      case ContentType.Articles:
        return authorArticles?.meta.pagination.total || 0;
      case ContentType.Dashboard:
        return authorDashboards ? 1 : 0;
      default:
        return 0;
    }
  };

  /**
   * Get the label for the current content type
   */
  const getContentLabel = () => {
    return activeTab;
  };

  /**
   * Get the icon for the active tab
   */
  const getActiveTabIcon = () => {
    const tab = contentTabs.find((tab) => tab.id === activeTab);
    return tab?.icon || contentTabs[0].icon;
  };

  return {
    activeTab,
    availableTabs,
    getContentCount,
    getContentLabel,
    getActiveTabIcon,
    handleTabChange,
  };
};
